/**
 * iter 73.10 — AnnouncementBanner
 *
 * Sits in AppShell (below TopBar) and renders active internal
 * announcements for the caller. Content is server-sanitised (bleach)
 * so `dangerouslySetInnerHTML` is safe here.
 *
 * Behaviour:
 *   • Fetches `/api/announcements/active` on mount + every 5 min.
 *   • Multiple active announcements are queued in priority order
 *     (critical → warning → info, newest first) — the server does
 *     that sort for us.
 *   • iter 73.10++ — Stacking logic: only the HIGHEST priority banner
 *     is shown by default; if there are more, a "+N more" chip on the
 *     right expands the stack vertically. When the top banner is
 *     dismissed, the next-highest one slides into its slot.
 *   • Every banner has a Dismiss button — even 'critical' — per spec.
 *     Dismissal calls `POST /api/announcements/{id}/dismiss` and hides
 *     the banner client-side; the server row remains.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { X, Info, Warning, WarningOctagon, CaretDown, CaretUp } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const PALETTE = {
  info:     { wrap: "bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-100",       Icon: Info },
  warning:  { wrap: "bg-amber-500/10 border-amber-500/40 text-amber-900 dark:text-amber-100",   Icon: Warning },
  critical: { wrap: "bg-red-500/10 border-red-500/40 text-red-900 dark:text-red-100",           Icon: WarningOctagon },
};

function BannerRow({ a, onDismiss, right, animate = false }) {
  const p = PALETTE[a.priority] || PALETTE.info;
  const Icon = p.Icon;
  return (
    <div
      role="status"
      className={`flex items-start gap-3 px-3.5 py-2.5 rounded border ${p.wrap} ${
        animate ? "animate-in fade-in slide-in-from-top-1 duration-200 ease-out" : ""
      }`}
      data-testid={`announcement-banner-${a.priority}-${a.id}`}
    >
      <Icon size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
      <div
        className="text-sm leading-snug flex-1 min-w-0 [&_a]:underline [&_a:hover]:opacity-80 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        /* content_html is already server-sanitised via bleach */
        dangerouslySetInnerHTML={{ __html: a.content_html }}
      />
      {right}
      <button
        type="button"
        className="btn btn-ghost btn-sm flex-shrink-0 opacity-70 hover:opacity-100"
        onClick={() => onDismiss(a.id)}
        title="Don't show again"
        data-testid={`announcement-dismiss-${a.id}`}
      >
        <X size={13} />
      </button>
    </div>
  );
}

export default function AnnouncementBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  // iter 73.10+ — track which ids we've already POSTed /view for to
  // avoid re-firing on every poll interval / re-render.
  const viewedRef = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get("/announcements/active");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      setItems([]);
    }
  }, [user?.id]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5 * 60 * 1000);   // 5-min poll
    return () => clearInterval(t);
  }, [refresh]);

  // iter 73.10+ — Fire read-receipt for each new item exactly once.
  useEffect(() => {
    items.forEach((a) => {
      if (viewedRef.current.has(a.id)) return;
      viewedRef.current.add(a.id);
      api.post(`/announcements/${a.id}/view`).catch(() => {
        viewedRef.current.delete(a.id);   // let a retry happen next mount
      });
    });
  }, [items]);

  // When the stack drains down to 0-1 items, auto-collapse so the "+N more"
  // chip doesn't linger with N=0 after a dismissal.
  useEffect(() => {
    if (items.length <= 1 && expanded) setExpanded(false);
  }, [items.length, expanded]);

  const dismiss = async (id) => {
    setItems((cur) => cur.filter((a) => a.id !== id));       // optimistic hide
    try { await api.post(`/announcements/${id}/dismiss`); } catch { /* ignore */ }
  };

  if (!items.length) return null;

  const [top, ...rest] = items;
  const hasMore = rest.length > 0;

  const moreChip = hasMore && (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
      title={expanded ? "Collapse" : `Show ${rest.length} more`}
      data-testid="announcement-more-toggle"
      aria-expanded={expanded}
    >
      {expanded ? (
        <>Hide <CaretUp size={10} weight="bold" /></>
      ) : (
        <>+{rest.length} more <CaretDown size={10} weight="bold" /></>
      )}
    </button>
  );

  return (
    <div className="flex flex-col gap-1.5 px-4 pt-2" data-testid="announcement-banner-stack">
      {/* Key on top.id — when the top banner changes (via dismissal chain),
          React unmounts the old row and mounts a new one, letting the
          `animate-in fade-in slide-in-from-top` classes trigger a subtle
          200ms entrance for the newly-promoted banner. */}
      <BannerRow key={top.id} a={top} onDismiss={dismiss} right={moreChip} animate />
      {expanded && rest.map((a) => (
        <BannerRow key={a.id} a={a} onDismiss={dismiss} animate />
      ))}
    </div>
  );
}
